import React from 'react';
import { Shield, Lock, FileText, Users, AlertCircle, Database, Network, Settings, CheckCircle, XCircle, Info, Scale, Gavel, BookOpen } from 'lucide-react';

const ZykainTermsOfService = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const StatusBadge = ({ status, text }) => {
    const config = {
      implemented: { icon: CheckCircle, colors: 'bg-green-100 text-green-800 border-green-200' },
      partial: { icon: Info, colors: 'bg-blue-100 text-blue-800 border-blue-200' },
      notApplicable: { icon: XCircle, colors: 'bg-gray-100 text-gray-600 border-gray-200' }
    };
    
    const { icon: Icon, colors } = config[status];

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${colors} whitespace-nowrap flex-shrink-0`}>
        <Icon className="h-3 w-3 mr-1 flex-shrink-0" />
        <span className="truncate">{text}</span>
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <a href="/" className="text-2xl text-gray-800 font-black tracking-wide">
                ZYKAIN
              </a>
            </div>
          </div>
        </div>
      </nav>

      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
          <div className="flex items-center justify-center mb-6">
            <Scale className="h-12 w-12 text-blue-600 mr-4" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">ZYKAIN</h1>
              <h2 className="text-xl font-semibold text-blue-600">Terms of Service</h2>
            </div>
          </div>
          <div className="text-center text-gray-600">
            <p>Last Updated: {currentDate}</p>
            <p>Version: 2025.1</p>
          </div>
        </div>

        {/* Quick Navigation Overview */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
            Legal Agreement Overview
          </h3>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
            <p className="text-blue-800">
              <strong>Legal Agreement:</strong> These Terms of Service govern your use of ZYKAIN's applications, 
              services, and website. By using our services, you agree to these terms and our policies.
            </p>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <a href="#overview" className="text-blue-600 hover:text-blue-700 font-medium">Overview</a>
            <a href="#services" className="text-blue-600 hover:text-blue-700 font-medium">Services</a>
            <a href="#usage" className="text-blue-600 hover:text-blue-700 font-medium">Usage Rules</a>
            <a href="#privacy" className="text-blue-600 hover:text-blue-700 font-medium">Privacy</a>
            <a href="#liability" className="text-blue-600 hover:text-blue-700 font-medium">Liability</a>
            <a href="#termination" className="text-blue-600 hover:text-blue-700 font-medium">Termination</a>
            <a href="#changes" className="text-blue-600 hover:text-blue-700 font-medium">Updates</a>
            <a href="#contact" className="text-blue-600 hover:text-blue-700 font-medium">Contact</a>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          
          {/* Overview */}
          <section id="overview" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-6 w-6 mr-2 text-blue-600" />
              1. Agreement Overview
            </h3>
            <div className="prose prose-blue max-w-none">
              <p className="text-gray-700 mb-4">
                Welcome to ZYKAIN! These Terms of Service ("Terms") govern your use of our applications, 
                services, and website. By using our services, you agree to these terms.
              </p>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <h5 className="font-medium text-yellow-800 mb-2">Important Requirements</h5>
                <ul className="text-yellow-700 text-sm space-y-1">
                  <li>• You must be at least 18 years old to use our services</li>
                  <li>• You must agree to these terms to access our services</li>
                  <li>• If you don't agree, please don't use our services</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Services */}
          <section id="services" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="h-6 w-6 mr-2 text-purple-600" />
              2. Our Services
            </h3>
            <div className="space-y-4">
              <p className="text-gray-700">
                ZYKAIN provides innovative technology solutions including:
              </p>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">App Development</span>
                  <p className="text-sm text-gray-600">Custom mobile and web applications for your business</p>
                </div>
                <StatusBadge status="implemented" text="Active Service" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">UI/UX Design</span>
                  <p className="text-sm text-gray-600">Beautiful, intuitive user interfaces and experiences</p>
                </div>
                <StatusBadge status="implemented" text="Active Service" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Atlassian Apps</span>
                  <p className="text-sm text-gray-600">Forge applications for the Atlassian Marketplace</p>
                </div>
                <StatusBadge status="implemented" text="Featured Service" />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Service Details:</h5>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>Atlassian Integration:</strong> Our Forge apps operate within Atlassian's infrastructure</li>
                  <li>• <strong>Compliance:</strong> All services comply with both Atlassian's terms and our terms</li>
                  <li>• <strong>Support:</strong> Professional support included with all services</li>
                  <li>• <strong>Updates:</strong> Regular updates and improvements to all offerings</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Usage Rules */}
          <section id="usage" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Shield className="h-6 w-6 mr-2 text-green-600" />
              3. Usage Rules & Guidelines
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h5 className="font-medium text-green-800 mb-2">✅ Permitted Use</h5>
                  <ul className="text-green-700 text-sm space-y-1">
                    <li>• Use services for legitimate business purposes</li>
                    <li>• Access features as intended</li>
                    <li>• Contact support for assistance</li>
                    <li>• Provide feedback for improvements</li>
                  </ul>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h5 className="font-medium text-red-800 mb-2">❌ Prohibited Use</h5>
                  <ul className="text-red-700 text-sm space-y-1">
                    <li>• Illegal activities or harmful conduct</li>
                    <li>• Hacking or system compromise attempts</li>
                    <li>• Unauthorized credential sharing</li>
                    <li>• Data collection without permission</li>
                  </ul>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="font-medium text-blue-800 mb-2">Example of Acceptable Use:</h5>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• Using our Atlassian app to improve team workflow</li>
                  <li>• Contacting support for technical assistance</li>
                  <li>• Providing feature requests and feedback</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Intellectual Property */}
          <section id="intellectual-property" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Lock className="h-6 w-6 mr-2 text-orange-600" />
              4. Intellectual Property Rights
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-orange-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Our Rights</span>
                  <p className="text-sm text-gray-600">Software, designs, documentation owned by ZYKAIN</p>
                </div>
                <StatusBadge status="implemented" text="Protected" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-orange-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Your Rights</span>
                  <p className="text-sm text-gray-600">You retain ownership of your content and data</p>
                </div>
                <StatusBadge status="implemented" text="User Owned" />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Rights Summary:</h5>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>License Model:</strong> You receive usage rights, not ownership of our technology</li>
                  <li>• <strong>Your Data:</strong> We only use your information as described in our Privacy Policy</li>
                  <li>• <strong>Respect:</strong> We respect intellectual property rights of others</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Privacy */}
          <section id="privacy" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Database className="h-6 w-6 mr-2 text-indigo-600" />
              5. Privacy & Data Protection
            </h3>
            <div className="space-y-4">
              <p className="text-gray-700">
                Your privacy is important to us. We collect and use your information in accordance with our Privacy Policy.
              </p>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-indigo-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Privacy Policy Compliance</span>
                  <p className="text-sm text-gray-600">All data handling follows our detailed Privacy Policy</p>
                </div>
                <StatusBadge status="implemented" text="Policy Linked" />
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="font-medium text-blue-800 mb-2">Privacy Reference:</h5>
                <p className="text-blue-700 text-sm">
                  For detailed information about how we handle your data, please review our{' '}
                  <a href="/privacy-policy" className="text-blue-600 hover:text-blue-700 underline font-medium">
                    Privacy Policy
                  </a>.
                </p>
              </div>
            </div>
          </section>

          {/* Liability */}
          <section id="liability" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <AlertCircle className="h-6 w-6 mr-2 text-yellow-600" />
              6. Disclaimers & Limitations
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-yellow-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Service Availability</span>
                  <p className="text-sm text-gray-600">High uptime goal, but maintenance may cause interruptions</p>
                </div>
                <StatusBadge status="partial" text="Best Effort" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-yellow-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Limitation of Liability</span>
                  <p className="text-sm text-gray-600">Liability limited to maximum extent permitted by law</p>
                </div>
                <StatusBadge status="implemented" text="Legal Protection" />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Liability Details:</h5>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>Service Level:</strong> We strive for reliability but cannot guarantee 100% uptime</li>
                  <li>• <strong>Damages:</strong> Not liable for indirect, incidental, or consequential damages</li>
                  <li>• <strong>Maintenance:</strong> Scheduled maintenance may temporarily affect availability</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Termination */}
          <section id="termination" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <XCircle className="h-6 w-6 mr-2 text-red-600" />
              7. Account Termination
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-red-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Termination by You</span>
                  <p className="text-sm text-gray-600">Stop using services anytime, contact us for data removal</p>
                </div>
                <StatusBadge status="implemented" text="User Choice" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-red-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Termination by Us</span>
                  <p className="text-sm text-gray-600">May suspend access for terms violations or security risks</p>
                </div>
                <StatusBadge status="partial" text="Policy Enforcement" />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Termination Process:</h5>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>Your Control:</strong> You can discontinue services at any time</li>
                  <li>• <strong>Data Handling:</strong> Contact us for account deletion or data removal assistance</li>
                  <li>• <strong>Policy Violations:</strong> We reserve the right to suspend access for violations</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Changes & Updates */}
          <section id="changes" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Network className="h-6 w-6 mr-2 text-purple-600" />
              8. Changes to Terms
            </h3>
            <div className="space-y-4">
              <p className="text-gray-700">
                We may update these terms from time to time. We'll notify you of significant changes 
                through our website or email.
              </p>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h5 className="font-medium text-purple-800 mb-2">Version Control:</h5>
                <ul className="text-purple-700 text-sm space-y-1">
                  <li>• <strong>Current Version:</strong> v2025.1</li>
                  <li>• <strong>Last Updated:</strong> January 1, 2025</li>
                  <li>• <strong>Next Review:</strong> July 1, 2025</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Governing Law */}
          <section id="governing-law" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Gavel className="h-6 w-6 mr-2 text-indigo-600" />
              9. Governing Law
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-indigo-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Jurisdiction</span>
                  <p className="text-sm text-gray-600">Governed by laws of India, Delhi jurisdiction</p>
                </div>
                <StatusBadge status="implemented" text="India/Delhi" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-indigo-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Dispute Resolution</span>
                  <p className="text-sm text-gray-600">Legal matters resolved in Delhi courts</p>
                </div>
                <StatusBadge status="implemented" text="Delhi Courts" />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Legal Framework:</h5>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>Governing Law:</strong> Laws of India apply to these terms</li>
                  <li>• <strong>Court Jurisdiction:</strong> Delhi courts have exclusive jurisdiction</li>
                  <li>• <strong>Official Language:</strong> English is the binding language for these terms</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-6 w-6 mr-2 text-blue-600" />
              10. Contact Information
            </h3>
            <div className="prose prose-blue max-w-none">
              <p className="text-gray-700 mb-4">
                For questions about these terms, legal matters, or general inquiries, 
                please contact us using the following information:
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Legal Contact</h4>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Email:</strong> <a href="mailto:hello@zykain.com" className="text-blue-600 hover:text-blue-700 underline">hello@zykain.com</a></li>
                  <li><strong>Company:</strong> ZYKAIN</li>
                  <li><strong>Location:</strong> Remote-first company based out of Delhi-NCR, India</li>
                  <li><strong>Response Time:</strong> We typically respond within 24-48 hours</li>
                </ul>
              </div>
            </div>
          </section>

        </div>

        {/* Final Note (before footer) */}
        <div className="bg-white p-6 mt-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-gray-600 text-sm mb-2">
                These terms of service reflect our current legal framework and commitment to transparent, 
                fair business practices with our clients and users.
              </p>
              <p className="text-gray-500 text-xs">
                © 2025 ZYKAIN Terms of Service. All rights reserved.
              </p>
            </div>
            
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 text-sm">
                <strong>Note:</strong> By using our services, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms of Service and our Privacy Policy.
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-white py-12" style={{ backgroundColor: '#16163F' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-2xl font-black tracking-wide mb-4">ZYKAIN</h3>
            <p className="text-gray-400 mb-6">Smart Apps, Smarter Solutions</p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-3">
              <a
                href="/privacy-policy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Privacy Policy
              </a>
              <a 
                href="/sprint-reporter-faq" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                FAQ
              </a>
              <a 
                href="/support" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Support
              </a>
              <a 
                href="/security-policy" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Security Policy
              </a>
              <a 
                href="/sla-policy" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Service Level Policy
              </a>
              <a
                href="/#contact"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Contact
              </a>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-800">
              <p className="text-gray-500 text-sm">
                © 2025 ZYKAIN. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ZykainTermsOfService;